import Error from 'next/error';

import { saleorAuthClient } from '@/graphql/client';
import { getUtmFieldsCamelCase } from '@/lib/campaignTracker';
import { paths } from '@/lib/paths';

export type AccountApiResponse<T> = {
  code: number;
  data?: T;
  message?: string;
  msg?: string;
};

export type AccountApiError = {
  code: number;
  msg?: string;
};

export type NotifyMeParam = {
  email: string;
  languageCode?: string;
  productLink: string;
  products: Product[];
  salutation: string;
  url_country_language_slug: string;
  websiteCountryCode: string;
};

export type Product = {
  price: number;
  productName: string;
  sku: string;
  theCurrency: string;
};

export type GuestNotifyMeRequestPayload = NotifyMeParam & {
  firstName: string;
  languageCode: string;
  lastName: string;
  recaptchaResponse: string;
};

export const useNotifyMe = (authClient = saleorAuthClient) => {
  const submitNotifyMe = async (
    param: NotifyMeParam,
  ): Promise<AccountApiResponse<NotifyMeParam>> => {
    const _formattedProducts = param.products!.map((item) => ({
      ...item,
      price: item.price.toString(),
    }));
    const utmFields = getUtmFieldsCamelCase(param.websiteCountryCode);
    try {
      const response = await authClient.fetchWithAuth(
        paths.api.notifyMe.user,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
          body: JSON.stringify({
            ...param,
            products: _formattedProducts,
            ...utmFields,
          }),
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message);
      }
      return response.json();
    } catch (error: any) {
      return {
        code: -1,
        msg:
          error.message ?? error.props ?? 'Failed to submit notify me for user',
      };
    }
  };

  const submitNotifyMeForGuest = async (
    param: GuestNotifyMeRequestPayload,
  ): Promise<AccountApiResponse<any>> => {
    const _formattedProducts = param.products.map((item) => ({
      ...item,
      price: item.price.toString(),
    }));
    const utmFields = getUtmFieldsCamelCase(param.websiteCountryCode);
    try {
      const response = await authClient.fetchWithAuth(
        paths.api.notifyMe.guestSubmit,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
          body: JSON.stringify({
            ...param,
            products: _formattedProducts,
            ...utmFields,
          }),
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message);
      }
      return response.json();
    } catch (error: any) {
      return {
        code: -1,
        msg:
          error.message ??
          error.props ??
          'Failed to submit notify me for guest',
      };
    }
  };

  const confirmNotifyMeForGuest = async (
    emailToken: string,
  ): Promise<AccountApiResponse<any>> => {
    try {
      const params = { emailToken };
      const response = await authClient.fetchWithAuth(
        `${paths.api.notifyMe.guestConfirm}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params),
          cache: 'no-store',
        },
        { allowPassingTokenToThirdPartyDomains: true },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message);
      }
      return response.json();
    } catch (error: any) {
      return {
        code: -1,
        msg:
          error.message ??
          error.props ??
          'Failed to confirm notify me for guest',
      };
    }
  };

  return {
    submitNotifyMe,
    submitNotifyMeForGuest,
    confirmNotifyMeForGuest,
  };
};
